import { useEffect, useState } from 'react';

import { setupSandboxController } from '@atlassian/bridge-core/v2';
import type { SandboxConnection } from '@atlassian/bridge-core/v2';
import type { ProductEnvironment } from '@atlassian/forge-ui-types';

import { getOrCreateSandboxIframe } from './SharedSandbox';
import { getForgeSandboxOrigin } from './utils';

export type SharedSandbox =
	| {
			connection: SandboxConnection;
			iframeEl: HTMLIFrameElement;
			ready: true;
	  }
	| {
			connection: null;
			iframeEl: HTMLIFrameElement | null;
			ready: false;
	  };

/**
 * Well-known key used to attach the connection promise directly to the iframe
 * DOM element.
 *
 * Storing it on the element means Module Federation / HMR cannot cause a
 * second handshake: any module instance that receives the same DOM node will
 * find the already-initiated promise.
 */
const SANDBOX_CONNECTION_KEY = '__forge-sandbox-connection-promise__';

type SandboxIframeElement = HTMLIFrameElement & {
	[SANDBOX_CONNECTION_KEY]?: Promise<SandboxConnection>;
};

/**
 * Tears down the shared connection and removes the promise from the element.
 * Should be called alongside {@link destroySandboxIframe} in test teardown.
 */
export const destroySandboxConnection = (iframe: HTMLIFrameElement): void => {
	const el = iframe as SandboxIframeElement;
	el[SANDBOX_CONNECTION_KEY]?.then((conn) => conn.destroy()).catch(() => {});
	delete el[SANDBOX_CONNECTION_KEY];
};

/**
 * Returns (and lazily initiates) the single shared sandbox connection promise.
 * The promise is stored on the iframe element so it survives across module
 * instances — only the first caller triggers the handshake.
 */
const getOrCreateConnectionPromise = (
	iframe: HTMLIFrameElement,
	contentWindow: Window,
	environment: ProductEnvironment,
): Promise<SandboxConnection> => {
	const el = iframe as SandboxIframeElement;
	const existing = el[SANDBOX_CONNECTION_KEY];
	if (existing) {
		return existing;
	}
	const promise = setupSandboxController(contentWindow, getForgeSandboxOrigin(environment));
	el[SANDBOX_CONNECTION_KEY] = promise;
	return promise;
};

/**
 * React hook that provides access to the shared, lazily-created sandbox iframe.
 *
 * The same iframe singleton is returned to every caller — mounting multiple
 * `WorkerWebRuntime` components will share a single `<iframe>` in the DOM.
 * Likewise, {@link setupSandboxController} is called at most once; all callers
 * receive the same {@link SandboxConnection}.
 *
 * ```tsx
 * const { connection } = useSharedSandbox(environment);
 * useEffect(() => {
 *   if (connection) {
 *     connection.controller.createApp(appContext);
 *   }
 * }, [connection]);
 * ```
 */
export const useSharedSandbox = (environment: ProductEnvironment): SharedSandbox => {
	const [connection, setConnection] = useState<SandboxConnection | null>(null);

	useEffect(() => {
		const iframe = getOrCreateSandboxIframe(environment);
		if (iframe === null) {
			// Non-browser environment (SSR) — nothing to do.
			return;
		}

		let cancelled = false;

		const initConnection = () => {
			const { contentWindow } = iframe;
			if (!contentWindow) {
				// Should not happen after the load event — guard for detached iframe edge case.
				return;
			}
			getOrCreateConnectionPromise(iframe, contentWindow, environment)
				.then((conn) => {
					if (!cancelled) {
						setConnection(conn);
					}
				})
				.catch(() => {
					// Handshake failed — connection remains null.
				});
		};
		// TODO: Will need a better way or API to detect when the sadbox is ready.
		if (iframe.contentDocument === null) {
			// contentDocument is null when the iframe has navigated to a cross-origin
			// page (i.e. the CDN sandbox has already loaded) — safe to connect now.
			initConnection();
		} else {
			// Still at about:blank (same-origin, contentDocument accessible) —
			// wait for the actual CDN src to load before attempting the handshake.
			iframe.addEventListener('load', initConnection, { once: true });
		}

		return () => {
			cancelled = true;
			iframe.removeEventListener('load', initConnection);
			// Do NOT destroy the shared connection on cleanup — other mounted
			// hook instances are still using it.
			setConnection(null);
		};
	}, [environment]);

	const iframeEl = getOrCreateSandboxIframe(environment);
	if (iframeEl && connection !== null) {
		return { ready: true, connection, iframeEl };
	}
	return { ready: false, connection: null, iframeEl };
};
